import { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { Button } from '../../components/admin/ui/Button';
import { Card } from '../../components/admin/ui/Card';
import { Input } from '../../components/admin/ui/Input';

interface Tag {
  id: string;
  name: string;
  slug: string;
  created_at: string;
}

export function BlogTagsPage() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchTags();
  }, []);

  async function fetchTags() {
    const { data } = await supabase
      .from('blog_tags')
      .select('*')
      .order('name');

    if (data) {
      setTags(data);
    }
    setLoading(false);
  }

  function generateSlug(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  function handleOpenModal(tag?: Tag) {
    if (tag) {
      setEditingId(tag.id);
      setName(tag.name);
      setSlug(tag.slug);
    } else {
      setEditingId(null);
      setName('');
      setSlug('');
    }
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setEditingId(null);
    setName('');
    setSlug('');
  }

  async function handleSave() {
    if (!name || !slug) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    if (editingId) {
      const { error } = await supabase
        .from('blog_tags')
        .update({ name, slug })
        .eq('id', editingId);

      if (error) {
        console.error('Error updating tag:', error);
        alert('Erreur lors de la mise à jour');
        return;
      }
    } else {
      const { error } = await supabase
        .from('blog_tags')
        .insert({ name, slug });

      if (error) {
        console.error('Error creating tag:', error);
        alert('Erreur lors de la création');
        return;
      }
    }

    handleCloseModal();
    fetchTags();
  }

  async function handleDelete(id: string) {
    const { error } = await supabase
      .from('blog_tags')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting tag:', error);
      alert('Erreur lors de la suppression');
    } else {
      fetchTags();
      setDeleteConfirm(null);
    }
  }

  if (loading) {
    return (
      <AdminLayout title="Tags" description="Gérez les tags des articles">
        <div className="p-8 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tags" description="Gérez les tags des articles">
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tags</h1>
          <p className="text-gray-600 mt-2">Gérez les tags des articles</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-5 h-5 mr-2" />
          Nouveau tag
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Création
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tags.map((tag) => (
                <tr key={tag.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {tag.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tag.slug}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(tag.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenModal(tag)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {deleteConfirm === tag.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(tag.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Confirmer
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(tag.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingId ? 'Modifier le tag' : 'Nouveau tag'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!editingId) {
                      setSlug(generateSlug(e.target.value));
                    }
                  }}
                  placeholder="Nom du tag"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug
                </label>
                <Input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(generateSlug(e.target.value))}
                  placeholder="slug-du-tag"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-4 mt-6">
              <Button variant="secondary" onClick={handleCloseModal}>
                Annuler
              </Button>
              <Button onClick={handleSave}>
                {editingId ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
}
